import type Store from "@ember-data/store";
import type BioExamObservation from "parog-web/models/loinc";
import parameterize from "parog-web/utils/parameterize";
import trimLoincCode from "parog-web/utils/trim-loinc-code";

export const DfgLoinc = "LOINC-62238-1";
export const RacLoinc = "LOINC-32294-1";

type OruStatusStringEnum =
  | undefined
  | "R"
  | "P"
  | "F"
  | "C"
  | "I"
  | "D"
  | "X"
  | "U";
export interface BioExamResultArgs {
  store?: Store;
  code: string;
  value: string;

  unit?: string | null;
  label?: string | null;

  originalLine?: OriginalLine;
  normIndicator?: string;
  normValInf?: string;
  normValSupp?: string;
  status?: string;
  selectedRawUnit?: string;

  oruStatus?: OruStatusStringEnum;
  isDraft?: boolean;
  matchType?: string;
  operator?: "equal-to" | "greater-than" | "lower-than";
}

export interface OriginalLine {
  raw?: RawFromHprim | RawFromOru;
  before_normalization?: BioExamResultArgs;
  copied_match_origin_uuid?: string;
  uuid: string;
  raw_label_options?: string;
}

export type RawFromHprim = {
  segs: string[];
};

type RawFromOru = {
  label: string;
  value: string;
  unit: string;
  "oru-status"?: OruStatusStringEnum;
};

export default class BioExamResult {
  store?: Store;

  resultHash: BioExamResultArgs;

  code: string;
  value: string;
  oruStatus: OruStatusStringEnum;
  isDraft?: boolean;
  matchType?: string;
  operator: "equal-to" | "greater-than" | "lower-than";
  selectedRawUnit?: string;

  readonly #label: string | null | undefined;
  readonly #unit: string | null | undefined;

  constructor(result: BioExamResultArgs) {
    this.resultHash = result;

    const {
      store,
      label,
      code,
      value,
      unit,
      oruStatus,
      isDraft,
      matchType,
      operator,
      selectedRawUnit
    } = result;

    this.code = code;
    this.value = value;
    this.oruStatus = oruStatus;
    this.#label = label;
    this.#unit = unit;
    this.isDraft = isDraft;
    this.matchType = matchType;
    this.operator = operator || "equal-to";
    this.selectedRawUnit = selectedRawUnit;

    this.store = store;
  }

  get label(): string | undefined | null {
    return this.bioExamObservation?.displayLabel || this.#label;
  }

  get unit(): string | undefined | null {
    // When the result is ignored isn't LOINC-ed, returns the raw unit
    if (this.isIgnored || this.isAdhoc) return this.#unit;

    // When the result is LOINC-ed, and the corresponding
    // observation's unit is null returns null
    if (!this.bioExamObservation?.unit) return null;

    // When the result is LOINC-ed, and the corresponding observation's
    // unit matches the raw unit, returns the observation's unit
    // OR
    // When the result is LOINC-ed, and the raw unit is null, return
    // the observation's unit
    if (
      !this.#unit ||
      this.#unit == "" ||
      this.#unit.toLowerCase() == this.bioExamObservation.unit?.toLowerCase()
    )
      return this.bioExamObservation.unit;

    // In any other cases returns the raw unit
    return this.#unit;
  }

  /**
   * @returns true if the result uses the "standard" unit.
   * The standard unit is the one Sêmeia uses.
   * It is usually the one from JDV Circuit Bio may differ sometimes.
   */
  get usesStandardUnit(): boolean {
    const standardUnit = this.bioExamObservation?.unit;
    if (this.bioExamObservation && !standardUnit) return true;

    return this.#unit?.toLowerCase() == standardUnit?.toLowerCase();
  }

  get hasTextValue(): boolean {
    return this.bioExamObservation?.valueType == "string";
  }

  get hasNoValueType(): boolean {
    return !this.bioExamObservation?.valueType;
  }

  /**
   * A coherent value is either:
   * - a string value if a string is expected. This is when hasTextValue is true.
   * - a number value if a number is expected.
   *   Some results are expected to be numbers but the parsing is not perfect yet and the value may contain text, which breaks Chart.js.
   *
   * This value is used to filter the displayed values in the graph.
   */
  get hasCoherentValue(): boolean {
    if (this.hasTextValue) return true;
    if (this.hasNoValueType) return true;
    if (!this.bioExamObservation) return true;
    // Passed this point this.value is expected to be a number.
    // parseFloat and +(something) may return a different content if this.value contains some text, meaning this is an incoherent value.
    return parseFloat(this.value) == +this.value;
  }

  get bioExamObservation(): BioExamObservation | null | undefined {
    if (trimLoincCode(this.code) == "ignore") return;

    return this.store?.peekRecord("loinc", trimLoincCode(this.code));
  }

  get isAdhoc(): boolean {
    return Boolean(this.code.match(/^ADHOC/)) || !this.bioExamObservation;
  }

  get isIgnored(): boolean {
    return trimLoincCode(this.code) == "ignore";
  }

  /**
   * A duplicate has the following format:
   * "ADHOC-6690-2-DUPLICATED"
   * Meaning it's a duplicate of the 6690-2 LOINC code
   */
  get isDuplicate(): boolean {
    return this.code.startsWith("ADHOC") && this.code.endsWith("DUPLICATED");
  }

  get rawUnit(): string | null | undefined {
    return (
      (this.resultHash.originalLine?.raw as RawFromHprim)?.segs?.[5] ||
      (this.resultHash.originalLine?.raw as RawFromOru).unit
    );
  }

  get matchedByExperience() {
    return this.matchType == "experience";
  }

  /**
   * Whether or not the result should be emphasized in the matching page.
   *
   * A result should be emphasized if
   *  - it has the draft status
   *  - its match type is different than `experience`, bc we consider
   *  - if its value should be a number considering the loinc matched
   *  a result matched by experience as not requiring the same attention
   *  - it's has been deduplicated by near-value
   */
  get shouldBeEmphasized() {
    if (!this.hasCoherentValue) {
      return true;
    }

    if (this.matchedByExperience) {
      return false;
    }

    if (this.isIgnored) {
      return false;
    }

    if (this.isDuplicate) {
      return false;
    }

    return this.isDraft;
  }

  get displayedValue() {
    let displayedValue;
    switch (this.operator) {
      case "greater-than":
        displayedValue = `≥ ${this.value}`;
        break;
      case "lower-than":
        displayedValue = `≤ ${this.value}`;
        break;
      default:
        displayedValue = this.value;
        break;
    }

    return displayedValue;
  }
  get isManualMatching() {
    return (
      (this.resultHash.originalLine?.raw as RawFromHprim)?.segs?.[0] ==
      "MANUAL RES"
    );
  }

  serialize(): BioExamResultArgs {
    return {
      code: this.code,
      value: this.value,
      unit: this.unit,
      label: this.label,
      originalLine: this.resultHash.originalLine,
      normIndicator: this.resultHash.normIndicator,
      normValInf: this.resultHash.normValInf,
      normValSupp: this.resultHash.normValSupp,
      status: this.resultHash.status,
      oruStatus: this.oruStatus,
      isDraft: this.isDraft,
      matchType: this.matchType,
      operator: this.operator,
      selectedRawUnit: this.selectedRawUnit
    };
  }

  static generateAdhocCode(rawMatch: string, reason: string | null = null) {
    let adhocCode = `ADHOC-${parameterize(rawMatch)}`;

    if (reason) {
      adhocCode += `-${reason}`;
    }
    return adhocCode;
  }

  static newManualRes(result: BioExamResultArgs): BioExamResult {
    const originalLine = {
      raw: {
        segs: [
          "MANUAL RES",
          "",
          "",
          "",
          result.value || "",
          result.unit || "",
          "",
          "",
          "",
          ""
        ]
      },
      uuid: crypto.randomUUID()
    };
    const params = Object.assign(result, { originalLine });

    return new BioExamResult(params);
  }
}
